import { useEffect, useRef, useState } from "react";
import { useTheme, THEMES, ThemeProvider, ThemeContext } from "../theme";

export { useTheme, THEMES, ThemeProvider, ThemeContext };

if (typeof document !== "undefined" && !document.getElementById("rbxwar-fonts")) {
  const style = document.createElement("style");
  style.id = "rbxwar-fonts";
  style.textContent = `
    @font-face { font-family: 'Khaviax';   src: url('/fonts/Khaviax.ttf')   format('truetype'); }
    @font-face { font-family: 'Lumiare';   src: url('/fonts/Lumiare.otf')   format('opentype'); }
    @font-face { font-family: 'Moderniz';  src: url('/fonts/Moderniz.otf')  format('opentype'); }
    @font-face { font-family: 'MADETommy'; src: url('/fonts/MADETommy.otf') format('opentype'); font-weight: 800; }
    @font-face { font-family: 'Profa';     src: url('/fonts/Profa.ttf')     format('truetype'); font-weight: 900; }
    @font-face { font-family: 'Rubik';     src: url('/fonts/Rubik.ttf')     format('truetype'); }
    @font-face { font-family: 'Rubik';     src: url('/fonts/Rubik-Italic.ttf') format('truetype'); font-style: italic; }

    .fade-up {
      opacity: 0;
      transform: translateY(20px);
      transition: opacity 0.65s ease, transform 0.65s ease;
    }
    .fade-up.visible { opacity: 1; transform: translateY(0); }

    @keyframes rowIn    { from { opacity: 0; } to { opacity: 1; } }
    @keyframes soldPop  { 0% { opacity:0; transform:scale(0.88); } 60% { opacity:1; transform:scale(1.04); } 100% { opacity:1; transform:scale(1); } }

    .rw-btn { transition: opacity 0.15s ease, filter 0.15s ease; cursor: pointer; }
    .rw-btn:hover { filter: brightness(1.08); }
    .rw-btn:active { opacity: 0.75; }
    .rw-link { transition: color 0.2s ease; }
    .rw-link:hover { color: #888 !important; }
    .rw-act-row { transition: border-color 0.2s ease, background 0.2s ease; }
    a.rw-act-row:hover { border-color: #2a2a2a !important; background: #0e0e0e !important; }
    @keyframes actIn {
      from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; transform: translateY(-10px); }
      to   { opacity: 1; max-height: 160px; padding-top: 16px; padding-bottom: 16px; transform: translateY(0); }
    }
    @keyframes actGlow {
      0%   { box-shadow: 0 0 0 1px var(--acc), 0 0 26px -4px var(--acc); border-color: var(--acc); }
      100% { box-shadow: 0 0 0 0 transparent; }
    }
    @keyframes actFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
    .rw-act-new  { overflow: hidden; animation: actIn 0.7s cubic-bezier(0.16,1,0.3,1) both, actGlow 2.6s ease-out both; }
    .rw-act-init { animation: actFade 0.5s ease both; animation-delay: var(--d, 0ms); }

    @keyframes logoGlow {
      0%, 100% { filter: drop-shadow(0 0 4px rgba(255,255,255,0.05)); }
      50%       { filter: drop-shadow(0 0 8px rgba(255,255,255,0.12)); }
    }
    .rw-logo { animation: logoGlow 4s ease-in-out infinite; transition: transform 0.5s cubic-bezier(0.19,1,0.22,1); cursor: pointer; }
    .rw-logo:hover { transform: scale(1.05); animation: none; }

    @keyframes floatMark {
      0%, 100% { transform: translateY(0px) rotate(-6deg); }
      50%       { transform: translateY(-18px) rotate(-6deg); }
    }
    .hero-mark { animation: floatMark 5s ease-in-out infinite; pointer-events: none; }
    .hero-bg {
      position: absolute; inset: -8px; z-index: 0; pointer-events: none;
      background: url(/hero-dashboard.webp) center top / cover no-repeat;
      opacity: 0.4;
      filter: blur(0.75px) brightness(1.6) contrast(1.1);
      -webkit-mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
      mask-image: linear-gradient(to bottom, #000 55%, transparent 100%);
    }
    .hero-scrim {
      position: absolute; inset: 0; z-index: 0; pointer-events: none;
      background: linear-gradient(90deg, rgba(9,9,9,0.5) 0%, rgba(9,9,9,0.3) 40%, rgba(9,9,9,0) 65%);
    }
    @media (max-width: 768px) {
      .hero-bg { background-size: 190% auto; }
      .hero-scrim { background: rgba(9,9,9,0.3); }
    }

    .plan-card { transition: border-color 0.2s ease, transform 0.2s ease; }
    .plan-card:hover { transform: translateY(-2px); }

    .feat-card:hover .feat-title { color: #fff !important; }
    .feat-card { transition: background 0.2s ease; }
    .feat-card:hover { background: #0d0d0d !important; }

    @media (max-width: 600px) {
      .rw-nav-btns { width: 100%; justify-content: center !important; gap: 12px !important; }
      .rw-nav-btns button { flex: 1; justify-content: center; }
    }
  `;
  document.head.appendChild(style);
}

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { ref.current.classList.add("visible"); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, [ref]);
}

function AnimatedHeadline({ children, delay = 0 }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => { const t = setTimeout(() => setVisible(true), delay); return () => clearTimeout(t); }, [delay]);
  const words = String(children).split(" ");
  let i = -1;
  return (
    <span style={{ display: "inline" }}>
      {words.map((word, wi) => (
        <span key={wi}>
          <span style={{ display: "inline-block", whiteSpace: "nowrap" }}>
            {word.split("").map((char) => {
              i++;
              const idx = i;
              return (
                <span key={idx} style={{
                  display: "inline-block",
                  opacity: visible ? 1 : 0,
                  transform: visible ? "translateY(0)" : "translateY(12px)",
                  transition: `opacity 0.35s ease ${idx * 24}ms, transform 0.35s ease ${idx * 24}ms`,
                }}>{char}</span>
              );
            })}
          </span>
          {wi < words.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
}

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789@#$%";
const SCRAMBLE_PHRASES = ["limited seller.", "pricewar bot.", "undercutter.", "repricing bot.", "reseller tool.", "way to list."];

function ScrambleText({ color }) {
  const [display, setDisplay] = useState("");
  const [phraseIdx, setPhraseIdx] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const phrase = SCRAMBLE_PHRASES[phraseIdx];
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const run = async () => {
      for (let typed = 0; typed <= phrase.length; typed++) {
        if (cancelled) return;
        let frame = phrase.slice(0, typed);
        const peek = Math.min(3, phrase.length - typed);
        for (let s = 0; s < peek; s++) frame += SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        setDisplay(frame);
        await sleep(35 + Math.random() * 20);
      }
      await sleep(2000);
      for (let remaining = phrase.length; remaining >= 0; remaining--) {
        if (cancelled) return;
        let frame = "";
        for (let i = 0; i < remaining; i++)
          frame += i < remaining * 0.55 ? phrase[i] : SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
        setDisplay(frame);
        await sleep(25 + Math.random() * 15);
      }
      await sleep(200);
      if (!cancelled) setPhraseIdx(i => (i + 1) % SCRAMBLE_PHRASES.length);
    };
    run();
    return () => { cancelled = true; };
  }, [phraseIdx]);
  return (
    <span style={{ color, display: "inline-block", minHeight: "1.1em", whiteSpace: "nowrap" }}>
      {display}
      <span style={{ display:"inline-block", width:3, height:"0.8em", background:color, marginLeft:3, verticalAlign:"middle", opacity:display.length%2===0?1:0.3, borderRadius:1, transition:"opacity 0.1s" }} />
    </span>
  );
}

function useCountUp(target, duration = 2200, active = false) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || !target) return;
    let raf, start = null;
    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const e = 1 - Math.pow(1 - p, 3);
      setVal(Math.floor(e * target));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, active, duration]);
  return val;
}

const TOP_ITEMS = [
  { name:"Telamon's Chicken Suit",   thumb:"https://tr.rbxcdn.com/180DAY-5ed7ad52ea28e5e765e64b0ce528c583/420/420/Hat/Png/noFilter", price:499998, tag:"All-Time #1" },
  { name:"Silver King of the Night", thumb:"https://tr.rbxcdn.com/180DAY-2db7aad67b23c47f6e341490861f2099/110/110/Hat/Webp/noFilter", rap:473411,   tag:"All-Time #2" },
  { name:"Ice Valkyrie",             thumb:"https://tr.rbxcdn.com/180DAY-334caaa5ba670a38f223d60e1af41aa4/420/420/Hat/Png/noFilter", price:354999, tag:"All-Time #3" },
];

function StatsSection({ theme }) {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  const [live, setLive] = useState({ value: 0, usd: 0 });
  const API = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setActive(true);
        if (ref.current) ref.current.classList.add("visible");
        obs.disconnect();
      }
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    fetch(`${API}/public/stats`).then(r => r.json()).then(d => {
      setLive({ value: d.live_inventory_value || 0, usd: d.live_inventory_usd || 0 });
    }).catch(() => {});
  }, []);

  const robux  = useCountUp(13_000_000, 2400, active);
  const invVal = useCountUp(live.value, 2600, active && live.value > 0);
  const invUsd = useCountUp(live.usd,   2600, active && live.usd   > 0);

  const fmt  = n => n.toLocaleString();
  const fmtU = n => "$" + n.toLocaleString(undefined, { maximumFractionDigits: 0 });

  const STATS = [
    { label:"robux earned on platform",      value: active ? fmt(robux) + " R$"                     : "-", sub:"and counting" },
    { label:"rolimons value in active bots", value: live.value > 0 ? fmt(invVal) + " R$"            : "-", sub:"across all accounts right now" },
    { label:"est. USD value running",        value: live.usd   > 0 ? fmtU(invUsd)                   : "-", sub:"live across the platform" },
  ];

  return (
    <section ref={ref} className="fade-up" style={{ padding:"80px 48px", borderTop:"1px solid #111" }}>
      <p style={s.label}>by the numbers</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:1, border:"1px solid #111", borderRadius:6, overflow:"hidden", marginBottom:48 }}>
        {STATS.map(stat => (
          <div key={stat.label} style={{ background:"#0b0b0b", padding:"28px 24px", borderRight:"1px solid #111" }}>
            <div style={{ fontSize:11, color:"#333", letterSpacing:2, textTransform:"uppercase", marginBottom:10, fontFamily:"'Moderniz',sans-serif" }}>{stat.label}</div>
            <div style={{ fontSize:"clamp(28px,3.5vw,42px)", fontWeight:800, color:"#fff", fontFamily:"'Profa',sans-serif", lineHeight:1, marginBottom:6 }}>{stat.value}</div>
            <div style={{ fontSize:11.5, color:"#2a2a2a" }}>{stat.sub}</div>
          </div>
        ))}
      </div>

      <div>
      <p style={s.label}>top items sold</p>
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(min(100%,300px),1fr))", gap:14 }}>
        {TOP_ITEMS.map((item, i) => (
          <div key={item.name} style={{ display:"flex", alignItems:"center", gap:16, background:"#0b0b0b", border:"1px solid #161616", borderRadius:10, padding:"16px 20px" }}>
            <div style={{ position:"relative", flexShrink:0 }}>
              <img src={item.thumb} alt={item.name} style={{ width:60, height:60, objectFit:"contain", background:"#111", borderRadius:8, display:"block" }} onError={e => e.target.style.display="none"} />
              <span style={{ position:"absolute", top:-6, left:-6, background:theme.accent, color:"#000", fontSize:8, fontWeight:800, padding:"2px 6px", borderRadius:4, letterSpacing:0.5 }}>#{i+1}</span>
            </div>
            <div style={{ minWidth:0 }}>
              <div style={{ color:"#fff", fontSize:17, fontWeight:700, marginBottom:4, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{item.name}</div>
              <div style={{ fontSize:11.5, color:"#444", marginBottom:6 }}>{item.tag}</div>
              <div style={{ fontSize:15, color:theme.accent, fontWeight:700, fontFamily:"'Profa',sans-serif" }}>{item.price != null
                ? <><span style={{ color:"#666", fontFamily:"inherit", fontWeight:400, fontSize:13 }}>sold for </span>{item.price.toLocaleString()} R$</>
                : <><span style={{ color:"#666", fontFamily:"inherit", fontWeight:400, fontSize:13 }}>RAP </span>{item.rap.toLocaleString()} R$</>}</div>
            </div>
          </div>
        ))}
      </div>
      </div>
    </section>
  );
}

function timeAgo(isoStr) {
  if (!isoStr) return "";
  const hasZone = /(Z|[+-]\d{2}(:?\d{2})?)$/.test(isoStr);
  const t = new Date(hasZone ? isoStr : isoStr + "Z").getTime();
  if (isNaN(t)) return "";
  const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
  if (diff < 5)     return "just now";
  if (diff < 60)    return `${diff}s ago`;
  if (diff < 3600)  return `${Math.floor(diff/60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
  return `${Math.floor(diff/86400)}d ago`;
}

function latestPerItem(events, max) {
  const seen = new Set();
  const out = [];
  for (const ev of events) {
    const key = ev.asset_id || ev.item_name;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(ev);
    if (out.length >= max) break;
  }
  return out;
}

const evKey = ev => `${ev.asset_id}:${ev.created_at}`;

function RecentActivity({ theme }) {
  const [events, setEvents] = useState([]);
  const [fresh, setFresh] = useState(() => new Set());
  const [, setTick] = useState(0);
  const seen = useRef(null);
  const initial = useRef(new Set());
  const API = process.env.REACT_APP_API_URL || "";

  useEffect(() => {
    let alive = true;
    const load = () =>
      fetch(`${API}/public/recent-activity`)
        .then(r => r.json())
        .then(d => {
          if (!alive) return;
          const list = latestPerItem(d.events || [], 3);
          const keys = list.map(evKey);
          if (seen.current === null) {
            seen.current = new Set(keys);
            initial.current = new Set(keys);
          } else {
            const added = keys.filter(k => !seen.current.has(k));
            if (added.length) {
              added.forEach(k => seen.current.add(k));
              setFresh(new Set(added));
              setTimeout(() => setFresh(prev => { const n = new Set(prev); added.forEach(k => n.delete(k)); return n; }), 3200);
            }
          }
          setEvents(list);
        })
        .catch(() => {});
    load();
    const poll = setInterval(load, 10000);
    const tick = setInterval(() => setTick(t => t + 1), 5000);
    return () => { alive = false; clearInterval(poll); clearInterval(tick); };
  }, []);

  if (!events.length) {
    return <div style={{ color:"#2a2a2a", fontSize:13, paddingTop:8 }}>repricing events will appear here once the bot runs.</div>;
  }

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
      {events.map((ev, i) => {
        const id = String(ev.asset_id || "");
        const k = evKey(ev);
        const linkable = /^\d+$/.test(id);
        const change = ev.old_price && ev.new_price ? ev.new_price - ev.old_price : 0;
        const cls = fresh.has(k) ? "rw-act-new" : initial.current.has(k) ? "rw-act-init" : "";
        const Row = linkable ? "a" : "div";
        const linkProps = linkable
          ? { href: `https://www.roblox.com/catalog/${id}`, target: "_blank", rel: "noopener noreferrer" }
          : {};
        return (
          <Row
            key={k}
            className={`rw-act-row ${cls}`}
            {...linkProps}
            style={{ "--acc": theme.accent, "--d": `${i * 70}ms`, display:"flex", alignItems:"center", gap:18, background:"#0b0b0b", border:"1px solid #161616", borderRadius:12, padding:"16px 20px", textDecoration:"none" }}
          >
            {ev.thumbnail
              ? <img src={ev.thumbnail} alt="" style={{ width:60, height:60, borderRadius:10, objectFit:"contain", background:"#111", flexShrink:0 }} onError={e => { e.target.style.visibility = "hidden"; }} />
              : <div style={{ width:60, height:60, borderRadius:10, background:"#111", flexShrink:0 }} />}
            <div style={{ minWidth:0, flex:1 }}>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", flexWrap:"wrap", gap:"4px 16px" }}>
                <span style={{ color:"#fff", fontSize:17, fontWeight:700, minWidth:0, maxWidth:"100%", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{ev.item_name}</span>
                <span style={{ display:"inline-flex", alignItems:"baseline", gap:8, flexShrink:0 }}>
                  {ev.old_price ? (
                    <>
                      <span style={{ color:"#666", fontSize:14, textDecoration:"line-through" }}>{ev.old_price.toLocaleString()}</span>
                      <span style={{ color:"#555", fontSize:14 }}>→</span>
                    </>
                  ) : null}
                  <span style={{ color:"#5bbf7f", fontSize:20, fontWeight:800, fontFamily:"'Profa',sans-serif" }}>{ev.new_price?.toLocaleString()}</span>
                  <span style={{ color:"#4a7a5a", fontSize:12 }}>R$</span>
                  {change !== 0 && (
                    <span style={{ background: change < 0 ? "rgba(91,191,127,0.12)" : "rgba(245,158,11,0.12)", color: change < 0 ? "#5bbf7f" : "#f59e0b", fontSize:12, fontWeight:700, padding:"2px 8px", borderRadius:6 }}>
                      {change < 0 ? "\u2212" : "+"}{Math.abs(change).toLocaleString()}
                    </span>
                  )}
                </span>
              </div>
              <div style={{ marginTop:6, fontSize:12.5, color:"#666" }}>
                updated {timeAgo(ev.created_at)}
                {linkable && <span style={{ color:theme.accent, fontWeight:700, marginLeft:10 }}>view live ↗</span>}
              </div>
            </div>
          </Row>
        );
      })}
    </div>
  );
}

function HeroActivity({ theme }) {
  return (
    <div style={{ width:"100%", maxWidth:460, background:"#0c0c0c", border:"1px solid #1a1a1a", borderRadius:14, padding:22 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, color:"#e5e5e5", fontSize:14, fontWeight:700 }}>
          <span style={{ width:7, height:7, borderRadius:"50%", background:theme.accent, boxShadow:`0 0 8px ${theme.accentGlow}` }} />
          recently updated
        </div>
        <span style={{ color:"#444", fontSize:11.5 }}>live</span>
      </div>
      <RecentActivity theme={theme} />
      <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid #161616", color:"#444", fontSize:11.5 }}>
        a live sample of repricing across the platform
      </div>
    </div>
  );
}

function Btn({ children, onClick, accent, outline }) {
  if (accent) return (
    <button className="rw-btn" onClick={onClick} style={{ background:accent, border:"none", color:"#000", borderRadius:8, padding:"11px 26px", fontSize:14, fontWeight:700, fontFamily:"inherit" }}>
      {children}
    </button>
  );
  return (
    <button className="rw-btn" onClick={onClick} style={{ background:"transparent", border:"1px solid #222", color:"#666", borderRadius:8, padding:"11px 26px", fontSize:14, fontFamily:"inherit" }}>
      {children}
    </button>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={s.faqItem} onClick={() => setOpen(o => !o)}>
      <div style={s.faqQ}>
        <span>{q}</span>
        <span style={{ ...s.faqChevron, transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>⌄</span>
      </div>
      <div style={{ ...s.faqA, maxHeight: open ? 200 : 0, opacity: open ? 1 : 0, marginTop: open ? 10 : 0 }}>{a}</div>
    </div>
  );
}

// ── Data ──────────────────────────────────────────────────────────────────────
const FEATURES = [
  { title:"fast price wars",  body:"scans your inventory and reprices in seconds, always one step ahead of whoever's undercutting you." },
  { title:"multi-account",    body:"run all your roblox accounts at once, each with its own items and settings." },
  { title:"smart floors",     body:"set floors per item or by tier, it'll never undersell something already selling on its own." },
  { title:"discord alerts",   body:"a ping for every sale, every price change, and a daily recap straight to your server." },
  { title:"2fa support",      body:"full totp two-factor handling so your accounts stay protected while the bot runs." },
  { title:"analytics",        body:"check your earnings, loop speed, and inventory value from one clean dashboard." },
];

const PLANS = [
  { name:"Lite",  price:"14.99", features:["up to 2 accounts","5 hand-picked items","medium speed","rap floor & min price","encrypted cookie & 2fa","discord webhooks","core settings"] },
  { name:"Basic", price:"34.99", features:["up to 5 accounts","20 hand-picked items","medium speed","valued item tier floors","encrypted cookie & 2fa","discord webhooks","full analytics"] },
  { name:"Pro",   price:"49.99", highlight:true, features:["unlimited accounts","autodiscover (finds items automatically)","fastest speed","every setting unlocked","floor decay","full analytics + inventory value","priority support"] },
];

const FAQS = [
  { q:"is this against roblox's terms of service?", a:"automating stuff on roblox falls outside their rules, and they can act on accounts they flag: trading restrictions, even termination. we can't stop that from happening. use rbxwar knowing that risk is on you. full details in our terms of service." },
  { q:"what happens if i lose my license key?", a:"the first time you activate a key, you get a recovery phrase, a one-time backup shown once. save it somewhere safe. if you lose both the key and the phrase, we can't verify it's you and can't get you back in." },
  { q:"can i cancel or switch plans anytime?", a:"yep. cancelling stops future renewals but won't refund the current period. switching plans happens through your plan provider." },
  { q:"do you store my roblox password?", a:"no, we never see it. we use your .roblosecurity session cookie instead, and it's encrypted before it's ever stored." },
  { q:"what's autodiscover?", a:"autodiscover (pro only) finds and prices every tradable item in your inventory automatically. on lite and basic you pick which items the bot manages." },
  { q:"how fast does it react?", a:"pro runs fastest, as low as a 5 second item check and a 15 second full loop. lite and basic run a bit slower to keep things fair across tiers." },
];

// ── Landing ───────────────────────────────────────────────────────────────────
export default function Landing({ onLogin, onBuy = () => window.open("https://rbxwar.mysellauth.com/","_blank"), onPrivacy, onTerms, isLoggedIn }) {
  const theme = useTheme();
  const featRef    = useRef(null);
  const pricingRef = useRef(null);
  const faqRef     = useRef(null);
  const ctaRef     = useRef(null);
  [featRef, pricingRef, faqRef, ctaRef].forEach(useFadeUp);

  return (
    <div style={s.root}>

      {/* Nav */}
      <nav style={s.nav}>
        <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0, flexShrink:1 }}>
          <img src="/logo.png" alt="RBXWar" className="rw-logo" style={{ height:40, width:"auto", flexShrink:0, filter: {
            green:     "grayscale(1) sepia(1) hue-rotate(102deg) saturate(4) brightness(0.95)",
            blue:      "grayscale(1) sepia(1) hue-rotate(157deg) saturate(3) brightness(0.95)",
            halloween: "grayscale(1) sepia(1) hue-rotate(345deg) saturate(4) brightness(1.0)",
            kawaii:    "grayscale(1) sepia(1) hue-rotate(280deg) saturate(3) brightness(1.0)",
            mono:      "grayscale(1) brightness(1.6)",
          }[theme.name] || "none" }} />
          <span style={{
            display:"inline-flex", alignItems:"center", gap:7,
            fontSize:10, fontWeight:800, letterSpacing:1.5, textTransform:"uppercase",
            padding:"4px 11px", borderRadius:20, whiteSpace:"nowrap",
            background:theme.accentBg, border:`1px solid ${theme.accentBorder}`,
            color:theme.accent, fontFamily:"'Moderniz',sans-serif", flexShrink:0,
          }}>
            <span style={{ width:6, height:6, borderRadius:"50%", background:theme.accent, boxShadow:`0 0 8px ${theme.accentGlow}` }} />
            fully launched
          </span>
        </div>
        <div className="rw-nav-btns" style={{ display:"flex", gap:8, alignItems:"center", flexShrink:0 }}>
          {isLoggedIn ? (
            <Btn accent={theme.accent} onClick={onLogin}>dashboard →</Btn>
          ) : (
            <>
              <Btn outline onClick={onLogin}>login</Btn>
              <Btn accent={theme.accent} onClick={onBuy}>buy access</Btn>
            </>
          )}
        </div>
      </nav>

      {/* Hero */}
      <div style={{ position:"relative", overflow:"hidden" }}>
        {/* dashboard screenshot backdrop */}
        <div className="hero-bg" aria-hidden="true" />
        <div className="hero-scrim" aria-hidden="true" />
      <div style={{ ...s.heroWrap, position:"relative", zIndex:1 }}>
        <div style={{ ...s.heroLeft, position:"relative" }}>
          <div style={{ position:"relative", zIndex:1 }}>
          <h1 style={s.headline}>
            <span style={{ display:"block" }}><AnimatedHeadline delay={200}>the fastest</AnimatedHeadline></span>
            <span style={{ display:"block", overflow:"hidden", whiteSpace:"nowrap" }}>
              <ScrambleText color={theme.accent} />
            </span>
          </h1>
          <p style={{ fontSize:"clamp(15px,1.6vw,19px)", color:"#888", margin:"12px 0 24px", lineHeight:1.5, fontWeight:400 }}>
            monitors your limiteds and instantly reprices when someone gets below you.
          </p>
          <div style={{ display:"flex", gap:12, marginBottom:52 }}>
            <Btn accent={theme.accent} onClick={onBuy}>buy access</Btn>
            {!isLoggedIn && <Btn outline onClick={onLogin}>login</Btn>}
          </div>
          <div style={s.pills}>
            {["< 15s reaction time", "24/7 uptime", "encrypted cookies & 2fa"].map(t => (
              <span key={t} style={s.pill}>{t}</span>
            ))}
          </div>
          </div>
        </div>
        <div style={s.heroRight}>
          <HeroActivity theme={theme} />
        </div>
      </div>
      </div>{/* end hero outer */}

      {/* Features */}
      <section ref={featRef} className="fade-up" style={{ ...s.section, position:"relative", overflow:"hidden" }}>
        {/* floating mark — desktop only, bleeds from true left edge */}
        <div className="hero-mark-wrap" style={{ position:"absolute", left:0, top:"50%", transform:"translateY(-50%)", zIndex:0, pointerEvents:"none" }}>
          <img
            src="/logo-mark.png"
            alt=""
            className="hero-mark"
            style={{ width:620, opacity:0.07, userSelect:"none", display:"block" }}
          />
        </div>
        <style>{`@media(max-width:768px){.hero-mark-wrap{display:none}}`}</style>
        <div style={{ position:"relative", zIndex:1 }}>
          <p style={s.label}>what it does</p>
          <div style={s.featGrid}>
            {FEATURES.map(f => (
              <div key={f.title} className="feat-card" style={s.featCard}>
                <div className="feat-title" style={s.featTitle}>{f.title}</div>
                <div style={s.featBody}>{f.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <StatsSection theme={theme} />

      {/* Pricing */}
      <section ref={pricingRef} className="fade-up" style={s.section}>
        <p style={s.label}>pricing</p>
        <h2 style={s.sectionH}>pick a plan</h2>
        <p style={s.sectionSub}>cancel anytime. switch plans whenever.</p>
        <div style={s.planGrid}>
          {PLANS.map(plan => (
            <div key={plan.name} className="plan-card" style={{ ...s.planCard, ...(plan.highlight ? { border:`1px solid ${theme.accent}`, background:`linear-gradient(160deg, ${theme.accentDark} 0%, #090909 60%)` } : {}) }}>
              {plan.highlight && (
                <div style={{ ...s.planBadge, background:theme.accent }}>most popular</div>
              )}
              <div style={{ ...s.planName, ...(plan.highlight ? { color:theme.accent } : {}) }}>{plan.name}</div>
              <div style={s.planPrice}>${plan.price}<span style={s.planPer}>/mo</span></div>
              <Btn accent={plan.highlight ? theme.accent : null} outline={!plan.highlight} onClick={onBuy}>
                get {plan.name.toLowerCase()}
              </Btn>
              <ul style={s.planList}>
                {plan.features.map(f => (
                  <li key={f} style={s.planFeature}>
                    <span style={{ color:plan.highlight ? theme.accent : "#444", flexShrink:0 }}>✓</span> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section ref={faqRef} className="fade-up" style={s.section}>
        <p style={{ ...s.label, textAlign:"center" }}>faq</p>
        <h2 style={{ ...s.sectionH, textAlign:"center" }}>questions</h2>
        <div style={s.faqWrap}>
          {FAQS.map(faq => <FaqItem key={faq.q} q={faq.q} a={faq.a} />)}
        </div>
      </section>

      {/* CTA */}
      <section ref={ctaRef} className="fade-up" style={{ ...s.section, textAlign:"center", paddingBottom:96 }}>
        <h2 style={{ ...s.sectionH, fontSize:"clamp(22px,3.5vw,38px)", marginBottom:12 }}>stop losing sales to slower sellers</h2>
        <p style={{ ...s.sectionSub, marginBottom:32 }}>get in now, no setup headaches.</p>
        <Btn accent={theme.accent} onClick={onBuy}>get started</Btn>
      </section>

      <footer style={s.footer}>
        <div style={s.footerLinks}>
          <a href="#" onClick={e=>{e.preventDefault();onPrivacy?.();}} style={s.footerLink} className="rw-link">privacy</a>
          <span style={{ color:"#1e1e1e" }}>·</span>
          <a href="#" onClick={e=>{e.preventDefault();onTerms?.();}} style={s.footerLink} className="rw-link">terms</a>
          <span style={{ color:"#1e1e1e" }}>·</span>
          <a href="https://discord.gg/683FrRFgsK" target="_blank" rel="noopener noreferrer" style={s.footerLink} className="rw-link">discord</a>
        </div>
        <div style={{ marginTop:8, color:"#2a2a2a" }}>© {new Date().getFullYear()} RBXWar</div>
      </footer>
    </div>
  );
}

const s = {
  root:       { minHeight:"calc(100vh / var(--ui-zoom, 1))", background:"#090909", color:"#fff", fontFamily:"'Rubik','Inter','Helvetica Neue',sans-serif", overflowX:"hidden" },

  nav:        { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 20px", borderBottom:"1px solid #111", position:"sticky", top:0, background:"#090909", zIndex:10, gap:10, flexWrap:"wrap" },

  heroWrap:   { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(300px,1fr))", alignItems:"center", gap:56, padding:"88px 48px 96px", maxWidth:1200, margin:"0 auto" },
  heroLeft:   { display:"flex", flexDirection:"column" },
  heroRight:  { display:"flex", justifyContent:"center" },
  headline:   { fontSize:"clamp(38px,5.2vw,66px)", fontWeight:800, lineHeight:1.04, margin:"0 0 22px", color:"#fff", fontFamily:"'Khaviax',sans-serif" },
  pills:      { display:"flex", flexWrap:"wrap", gap:8 },
  pill:       { fontSize:11.5, color:"#444", background:"#111", border:"1px solid #1a1a1a", borderRadius:20, padding:"5px 12px" },

  section:    { padding:"80px 48px", borderTop:"1px solid #111" },
  label:      { fontSize:10.5, letterSpacing:2.5, color:"#333", textTransform:"uppercase", marginBottom:16, fontFamily:"'Moderniz',sans-serif" },
  sectionH:   { fontSize:"clamp(22px,3.5vw,36px)", fontWeight:800, color:"#fff", margin:"0 0 8px", fontFamily:"'Khaviax',sans-serif" },
  sectionSub: { color:"#3a3a3a", fontSize:14.5, margin:"0 0 44px" },

  featGrid:   { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))", gap:1, border:"1px solid #111", borderRadius:6, overflow:"hidden" },
  featCard:   { background:"#0b0b0b", padding:"26px 22px", borderRight:"1px solid #111", borderBottom:"1px solid #111" },
  featTitle:  { color:"#888", fontSize:13, fontWeight:700, marginBottom:8, fontFamily:"'Moderniz',sans-serif", transition:"color 0.2s ease" },
  featBody:   { color:"#3a3a3a", fontSize:13, lineHeight:1.65 },

  planGrid:   { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:14, maxWidth:960, margin:"0 auto" },
  planCard:   { background:"#0b0b0b", border:"1px solid #161616", borderRadius:8, padding:"26px 22px", position:"relative" },
  planBadge:  { position:"absolute", top:-11, left:18, color:"#000", fontSize:10, fontWeight:800, padding:"3px 10px", borderRadius:20, letterSpacing:0.5 },
  planName:   { color:"#444", fontSize:10.5, fontWeight:700, letterSpacing:2, textTransform:"uppercase", marginBottom:8, fontFamily:"'Moderniz',sans-serif" },
  planPrice:  { fontSize:34, fontWeight:800, color:"#fff", fontFamily:"'Profa',sans-serif", margin:"0 0 18px", lineHeight:1 },
  planPer:    { fontSize:13, color:"#333", fontFamily:"'Rubik',sans-serif", fontWeight:400 },
  planList:   { listStyle:"none", padding:0, margin:"20px 0 0", display:"flex", flexDirection:"column", gap:10 },
  planFeature:{ display:"flex", gap:8, color:"#555", fontSize:12.5, alignItems:"flex-start", lineHeight:1.4 },

  faqWrap:    { maxWidth:680, width:"100%", margin:"0 auto", display:"flex", flexDirection:"column", gap:6 },
  faqItem:    { background:"#0b0b0b", border:"1px solid #161616", borderRadius:6, padding:"15px 18px", cursor:"pointer" },
  faqQ:       { display:"flex", justifyContent:"space-between", alignItems:"center", color:"#ccc", fontSize:13.5, fontWeight:500, gap:16 },
  faqChevron: { color:"#333", fontSize:16, flexShrink:0, transition:"transform 0.25s ease" },
  faqA:       { color:"#555", fontSize:13, lineHeight:1.65, overflow:"hidden", transition:"max-height 0.3s ease, opacity 0.25s ease, margin-top 0.3s ease" },

  footer:     { borderTop:"1px solid #111", padding:"22px 48px", textAlign:"center" },
  footerLinks:{ display:"flex", justifyContent:"center", alignItems:"center", gap:12, marginBottom:6 },
  footerLink: { color:"#2a2a2a", fontSize:12, textDecoration:"none" },
};
